// @ts-nocheck
import { Storage, restoreActiveProfileFromSession } from "./setting";

const digits = (value) => String(value ?? "").replace(/\D/g, "");

const PLACEHOLDERS = new Set(["did", "number", "originaldid"]);

const isBlank = (v) =>
  v == null ||
  v === "" ||
  v === "undefined" ||
  v === "null" ||
  (typeof v === "string" && v.trim() === "") ||
  (typeof v === "string" && PLACEHOLDERS.has(v.trim().toLowerCase()));

function getPath(obj, path) {
  if (!obj || typeof obj !== "object") return undefined;
  return path.split(".").reduce((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return acc[key];
  }, obj);
}

function firstByPaths(obj, paths) {
  for (const p of paths) {
    const v = getPath(obj, p);
    if (!isBlank(v)) return v;
  }
  return undefined;
}

function deepFindByKeys(obj, keys, depth = 0, seen = new Set()) {
  if (!obj || typeof obj !== "object" || depth > 6 || seen.has(obj)) return undefined;
  seen.add(obj);
  const want = keys.map((k) => k.toLowerCase());

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = deepFindByKeys(item, keys, depth + 1, seen);
      if (!isBlank(found)) return found;
    }
    return undefined;
  }

  for (const [key, value] of Object.entries(obj)) {
    if (want.includes(String(key).toLowerCase()) && !isBlank(value) && typeof value !== "object") {
      return value;
    }
  }
  for (const value of Object.values(obj)) {
    if (value && typeof value === "object") {
      const found = deepFindByKeys(value, keys, depth + 1, seen);
      if (!isBlank(found)) return found;
    }
  }
  return undefined;
}

function resolve(obj, paths, keys) {
  const byPath = firstByPaths(obj, paths);
  if (!isBlank(byPath)) return byPath;
  const byKey = deepFindByKeys(obj, keys || []);
  return isBlank(byKey) ? null : byKey;
}

export function getActiveTelephonyProfile() {
  try {
    restoreActiveProfileFromSession();
    const raw = Storage.getItem("user");
    if (!raw) return null;
    const user = JSON.parse(raw);
    const profiles = user?.agentData?.agentTelephony?.profile;
    if (!Array.isArray(profiles) || !profiles.length) return null;
    return profiles.find((p) => p?.active_profile === true) || null;
  } catch {
    return null;
  }
}

let mobileHangupInFlight = null;
export function runMobileHangupOnce(post) {
  if (mobileHangupInFlight) return Promise.resolve(null);
  mobileHangupInFlight = Promise.resolve()
    .then(post)
    .finally(() => { mobileHangupInFlight = null; });
  return mobileHangupInFlight;
}

export function isMobileProfileActive() {
  const profile = getActiveTelephonyProfile();
  return String(profile?.type || "").trim() === "Mobile";
}

export function getActiveLoginType() {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      const t = window.sessionStorage.getItem("activeLoginType");
      if (t) return t;
    }
  } catch {
  }
  const profile = getActiveTelephonyProfile();
  return profile?.type || null;
}

export function isWebrtcProfileActive() {
  const t = String(getActiveLoginType() || "").trim().toUpperCase();
  return t === "WEBRTC" || t === "SOFT PHONE";
}

const WEBRTC_CUSTOMER_TTL_MS = 15000;
let webrtcCustomer = null;

export function rememberWebrtcCustomer(entry) {
  webrtcCustomer = entry?.number ? { ...entry, at: Date.now() } : null;
}

export function recallWebrtcCustomer({ traceId, channelId } = {}) {
  const c = webrtcCustomer;
  if (!c || Date.now() - c.at > WEBRTC_CUSTOMER_TTL_MS) return null;
  if (traceId && c.traceId && traceId !== c.traceId) return null;
  if (channelId && c.channelIds?.length && !c.channelIds.includes(channelId)) return null;
  return c.number;
}

export function isSocketCallDrivenProfileActive() {
  return isMobileProfileActive();
}

export function resolveEventName(payload) {
  const v = resolve(
    payload,
    ["eventName", "event_name", "event.type", "type", "name"],
    ["eventName", "event_name"]
  );
  return v ? String(v).toUpperCase() : null;
}

export function resolveAgentId(payload) {
  return resolve(payload, ["agentId", "agent_id", "agent.id"], ["agentId", "agent_id"]);
}

export function resolveAgentNumber(payload) {
  return resolve(
    payload,
    ["agentNumber", "agent_number", "agent.number", "agent.number.number"],
    ["agentNumber", "agent_number"]
  );
}

export function resolveChannelId(payload) {
  return resolve(
    payload,
    ["channelId", "channel_id", "channel.id", "event.channel.id", "agentChannelId"],
    ["channelId", "channel_id"]
  );
}

export function resolveAgentChannelId(payload) {
  return resolve(
    payload,
    ["agentChannelId", "agent_channel_id"],
    ["agentChannelId", "agent_channel_id"]
  );
}

export function resolveCustomerChannelId(payload) {
  return resolve(
    payload,
    ["customerChannelId", "customer_channel_id"],
    ["customerChannelId", "customer_channel_id"]
  );
}

export function resolveCallGroupId(payload) {
  return resolve(
    payload,
    ["callGroupId", "call_group_id", "X-NS-CallGroupId", "X_NS_CallGroupId"],
    ["callGroupId", "call_group_id"]
  );
}

export function resolveCampaignId(payload) {
  return resolve(
    payload,
    [
      "campaignDetails.campaignId",
      "campaignDetails.campaign_id",
      "campaign_details.campaignId",
      "campaign_details.campaign_id",
      "campaignId",
      "campaign_id",
      "X-NS-CampaignId",
      "X_NS_CampaignId",
    ],
    ["campaignId", "campaign_id"]
  );
}

export function resolveLeadData(payload, contactNumber = null) {
  const firstName = resolve(
    payload,
    ["firstName", "first_name", "lead.first_name", "leadData.first_name", "caller_profile.first_name"],
    ["firstName", "first_name"]
  );
  const lastName = resolve(
    payload,
    ["lastName", "last_name", "lead.last_name", "leadData.last_name", "caller_profile.last_name"],
    ["lastName", "last_name"]
  );
  const company = resolve(
    payload,
    ["company", "companyName", "company_name", "lead.company", "leadData.company"],
    ["company", "company_name"]
  );
  if (isBlank(firstName) && isBlank(lastName) && isBlank(company)) return null;
  return {
    first_name: isBlank(firstName) ? null : firstName,
    last_name: isBlank(lastName) ? null : lastName,
    company: isBlank(company) ? null : company,
    contact_number: contactNumber || null,
  };
}

export function resolveTimestamp(payload) {
  return (
    resolve(
      payload,
      [
        "timestamp",
        "eventTimestamp",
        "event_timestamp",
        "event.timestamp",
        "channel.creationtime",
      ],
      ["timestamp", "eventTimestamp"]
    ) || new Date().toISOString()
  );
}

export function resolveTraceId(payload) {
  return resolve(payload, ["traceId", "trace_id"], ["traceId", "trace_id"]);
}

export function resolveCallLeg(payload) {
  const v = resolve(payload, ["callLeg", "call_leg"], ["callLeg", "call_leg"]);
  return v ? String(v).toUpperCase() : null;
}

export function resolveCustomerNumber(payload) {
  return resolve(
    payload,
    ["customerNumber", "customer_number"],
    ["customerNumber", "customer_number"]
  );
}

export function resolveAccountCode(payload) {
  return resolve(
    payload,
    ["accountCode", "account_code", "channel.accountcode"],
    ["accountCode", "account_code", "accountcode"]
  );
}

export function resolveAgentName(payload) {
  return resolve(payload, ["agentName", "agent_name"], ["agentName", "agent_name"]);
}

export function resolveHangupCause(payload) {
  return resolve(
    payload,
    ["causeReason", "cause_reason", "hangup_cause", "hangupCause", "event.cause_txt"],
    ["causeReason", "hangup_cause"]
  );
}

export function resolveCounterpartyNumber(payload) {
  return resolve(
    payload,
    [
      "did",
      "originalDid",
      "callerNumber",
      "caller_number",
      "from",
      "number",
    ],
    []
  );
}

export const CALL_TYPES = {
  INBOUND: "INBOUND",
  OUTBOUND: "OUTBOUND",
  TRANSFER_BLIND: "TRANSFER_BLIND",
  TRANSFER_ATTENDED: "TRANSFER_ATTENDED",
  CONFERENCE: "CONFERENCE",
  BARGE: "BARGE",
  WHISPER: "WHISPER",
  MONITOR: "MONITOR",
  UNKNOWN: "UNKNOWN",
};

export function resolveCallType(payload) {
  const v = resolve(payload, ["callType", "call_type"], ["callType", "call_type"]);
  return v ? String(v).trim().toUpperCase() : null;
}

export function resolveIsIvrSource(payload) {
  const v = resolve(
    payload,
    ["is_ivr_source", "isIvrSource"],
    ["is_ivr_source", "isIvrSource"]
  );
  if (v === true) return true;
  const s = String(v ?? "").trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

export function deriveCallTypeFlags({ callType, isIvrSource, isAgentLead } = {}) {
  const t = String(callType || "").trim().toUpperCase();
  return {
    is_transfer:
      t === CALL_TYPES.TRANSFER_BLIND || t === CALL_TYPES.TRANSFER_ATTENDED,
    is_conference: t === CALL_TYPES.CONFERENCE,
    is_barge_in: t === CALL_TYPES.BARGE,
    is_whisper: t === CALL_TYPES.WHISPER,
    is_monitor: t === CALL_TYPES.MONITOR,
    is_ivr: Boolean(isIvrSource),
    is_agent_lead: Boolean(isAgentLead),
  };
}

export function resolveDirection(payload) {
  const rtRaw = resolve(
    payload,
    ["routingType", "routing_type", "direction", "callDirection", "call_direction"],
    ["routingType", "direction"]
  );
  const rt = String(rtRaw || "").trim().toLowerCase();
  if (rt === "outgoing" || rt === "outbound" || rt === "out") return "outgoing";
  if (rt === "incoming" || rt === "inbound" || rt === "in") return "incoming";
  if (resolveEventName(payload) === "DIALING") return "outgoing";
  return "incoming";
}

export function eventBelongsToAgent(payload, { agentId, agentNumber } = {}) {
  if (!payload) return false;

  const evtAgentId = resolveAgentId(payload);
  const evtAgentNumber = digits(resolveAgentNumber(payload));

  if (!isBlank(evtAgentId) && agentId != null && String(evtAgentId) === String(agentId)) {
    return true;
  }
  const myNumber = digits(agentNumber);
  if (evtAgentNumber && myNumber && evtAgentNumber.slice(-10) === myNumber.slice(-10)) {
    return true;
  }

  const hasId = !isBlank(evtAgentId);
  if (!hasId && !evtAgentNumber) return true;

  return false;
}

export function normalizeCallEvent(payload, fallbackEventName) {
  return {
    eventName: resolveEventName(payload) || (fallbackEventName ? String(fallbackEventName).toUpperCase() : null),
    agentId: resolveAgentId(payload),
    agentNumber: resolveAgentNumber(payload),
    direction: resolveDirection(payload),
    number: resolveCounterpartyNumber(payload),
    channelId: resolveChannelId(payload),
    agentChannelId: resolveAgentChannelId(payload),
    customerChannelId: resolveCustomerChannelId(payload),
    callLeg: resolveCallLeg(payload),
    accountCode: resolveAccountCode(payload),
    agentName: resolveAgentName(payload),
    customerNumber: resolveCustomerNumber(payload),
    callGroupId: resolveCallGroupId(payload),
    campaignId: resolveCampaignId(payload),
    leadData: resolveLeadData(payload, resolveCounterpartyNumber(payload)),
    timestamp: resolveTimestamp(payload),
    traceId: resolveTraceId(payload),
    hangupCause: resolveHangupCause(payload),
    callType: resolveCallType(payload),
    isIvrSource: resolveIsIvrSource(payload),
  };
}
